import React from "react";
import { cn } from "@/shared/utils/shadcn";

interface Point {
  x: number;
  y: number;
}

interface Score extends Point {}

interface LinePoint {
  bezMinX: number;
  bezMinY: number;
  bezMaxX: number;
  bezMaxY: number;
  scoreX: number;
  scoreY: number;
}

interface BlobProps {
  wedgeAngle: number;
  midX: number;
  midY: number;
  isMemberHover?: boolean;
  isMember?: boolean;
  sectorRadius: number;
  scores: Score[];
}

const lineDistance = (point1: Point, point2: Point): number => {
  const xs = (point2.x - point1.x) ** 2;
  const ys = (point2.y - point1.y) ** 2;
  return Math.sqrt(xs + ys);
};

const apList = <T, U>(fs: ((x: T) => U)[], xs: T[]): U[] =>
  fs.flatMap((f) => xs.map((x) => f(x)));

const fst = <T, U>(tpl: [T, U]): T => tpl[0];
const snd = <T, U>(tpl: [T, U]): U => tpl[1];

type Point2D = [number, number];

const intersection = (
  ab: [Point2D, Point2D],
  pq: [Point2D, Point2D]
): Point2D => {
  if (ab[0][0] === ab[1][0] && ab[0][1] === ab[1][1]) {
    return ab[0];
  }

  const delta =
    (f: (x: Point2D) => number) =>
    (x: [Point2D, Point2D]): number =>
      f(x[0]) - f(x[1]);

  const [abDX, pqDX, abDY, pqDY] = apList([delta(fst), delta(snd)], [ab, pq]);

  const determinant = abDX * pqDY - abDY * pqDX;

  const [abD, pqD] = [ab, pq].map(
    ([a, b]) => fst(a) * snd(b) - fst(b) * snd(a)
  );

  return apList(
    [([pq, ab]: [number, number]) => (abD * pq - ab * pqD) / determinant],
    [
      [pqDX, abDX],
      [pqDY, abDY],
    ]
  ) as Point2D;
};

export const Blob: React.FC<BlobProps> = ({
  wedgeAngle,
  midX,
  midY,
  isMemberHover,
  isMember,
  sectorRadius,
  scores,
}) => {
  const line: LinePoint[] = [];
  const maxBezDistance = sectorRadius;

  scores.forEach((score, i) => {
    const startAngle = wedgeAngle * i;
    const scoreAngle = startAngle + wedgeAngle / 2;

    const prev = i - 1 === -1 ? scores.length - 1 : i - 1;
    const next = i + 1 === scores.length ? 0 : i + 1;

    const scoreMaxX = midX + 100 * Math.cos((Math.PI * scoreAngle) / 180);
    const scoreMaxY = midY + 100 * Math.sin((Math.PI * scoreAngle) / 180);

    const prevX = scores[prev].x;
    const prevY = scores[prev].y;

    const nextX = scores[next].x;
    const nextY = scores[next].y;

    let [intersectionX, intersectionY] = intersection(
      [
        [prevX, prevY],
        [nextX, nextY],
      ],
      [
        [midX, midY],
        [scoreMaxX, scoreMaxY],
      ]
    );

    if (intersectionX === 50) intersectionX = 51;
    if (intersectionY === 50) intersectionY = 51;

    const projAngle =
      (Math.atan2(score.y - intersectionY, score.x - intersectionX) * 180) /
      Math.PI;

    const projDistance = lineDistance(
      { x: score.x, y: score.y },
      { x: intersectionX, y: intersectionY }
    );

    const prevProjX =
      prevX + projDistance * Math.cos((Math.PI * projAngle) / 180);
    const prevProjY =
      prevY + projDistance * Math.sin((Math.PI * projAngle) / 180);
    const nextProjX =
      nextX + projDistance * Math.cos((Math.PI * projAngle) / 180);
    const nextProjY =
      nextY + projDistance * Math.sin((Math.PI * projAngle) / 180);

    const bezMinInterX =
      midX + 100 * Math.cos((Math.PI * (scoreAngle - wedgeAngle / 5)) / 180);
    const bezMinInterY =
      midY + 100 * Math.sin((Math.PI * (scoreAngle - wedgeAngle / 5)) / 180);

    const bezMaxInterX =
      midX + 100 * Math.cos((Math.PI * (scoreAngle + wedgeAngle / 5)) / 180);
    const bezMaxInterY =
      midY + 100 * Math.sin((Math.PI * (scoreAngle + wedgeAngle / 5)) / 180);

    const maxBezMinInterX =
      midX +
      maxBezDistance *
        Math.cos((Math.PI * (scoreAngle - wedgeAngle / 1.5)) / 180);
    const maxBezMinInterY =
      midY +
      maxBezDistance *
        Math.sin((Math.PI * (scoreAngle - wedgeAngle / 1.5)) / 180);

    const maxBezMaxInterX =
      midX +
      maxBezDistance *
        Math.cos((Math.PI * (scoreAngle + wedgeAngle / 1.5)) / 180);
    const maxBezMaxInterY =
      midY +
      maxBezDistance *
        Math.sin((Math.PI * (scoreAngle + wedgeAngle / 1.5)) / 180);

    const [bezMinX, bezMinY] = intersection(
      [
        [prevProjX, prevProjY],
        [nextProjX, nextProjY],
      ],
      [
        [midX, midY],
        [bezMinInterX, bezMinInterY],
      ]
    );

    const [bezMaxX, bezMaxY] = intersection(
      [
        [prevProjX, prevProjY],
        [nextProjX, nextProjY],
      ],
      [
        [midX, midY],
        [bezMaxInterX, bezMaxInterY],
      ]
    );

    const bezMinDistance = lineDistance(
      { x: bezMinX, y: bezMinY },
      { x: midX, y: midY }
    );

    const bezMaxDistance = lineDistance(
      { x: bezMaxX, y: bezMaxY },
      { x: midX, y: midY }
    );

    const maxBezCondition =
      bezMinDistance > maxBezDistance || bezMaxDistance > maxBezDistance;

    line.push({
      bezMinX: maxBezCondition ? maxBezMinInterX : bezMinX,
      bezMinY: maxBezCondition ? maxBezMinInterY : bezMinY,
      bezMaxX: maxBezCondition ? maxBezMaxInterX : bezMaxX,
      bezMaxY: maxBezCondition ? maxBezMaxInterY : bezMaxY,
      scoreX: score.x,
      scoreY: score.y,
    });
  });

  const curve = line.map((s, i) => {
    const next = i + 1 === line.length ? 0 : i + 1;
    if (i === 0) {
      return `M${s.scoreX},${s.scoreY} C${s.bezMaxX},${s.bezMaxY} ${line[next].bezMinX},${line[next].bezMinY} ${line[next].scoreX},${line[next].scoreY}`;
    }
    return `S${line[next].bezMinX},${line[next].bezMinY} ${line[next].scoreX},${line[next].scoreY}`;
  });

  return (
    <path
      className={cn(
        "transition-all duration-300 ease-in-out fill-primary",
        isMemberHover ? "fill-gray-300" : "",
        isMember ? "overflow-visible" : ""
      )}
      d={`${curve.join(" ")}`}
    />
  );
};

export default Blob;
